import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { content } from "../lib/i18n";
import { useLang } from "../context/LanguageContext";

const Testimonials = () => {
  const { lang } = useLang();
  const fr = content.testimonials.fr;
  const en = content.testimonials.en;
  const isBoth = lang === "both";
  const items = (lang === "en" ? en : fr).items;

  const [index, setIndex] = useState(0);

  useEffect(() => {
    const t = setInterval(() => {
      setIndex((i) => (i + 1) % items.length);
    }, 6500);
    return () => clearInterval(t);
  }, [items.length]);

  useEffect(() => {
    setIndex(0);
  }, [lang]);

  const next = () => setIndex((i) => (i + 1) % items.length);
  const prev = () => setIndex((i) => (i - 1 + items.length) % items.length);

  const current = items[index];
  const altCurrent = isBoth ? en.items[index] : null;

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="bg-white section"
    >
      <div className="mx-auto max-w-[1320px] px-5 md:px-8 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-10 md:mb-14">
          <div>
            <span className="eyebrow-yellow">
              <Star size={12} strokeWidth={2.5} fill="currentColor" />
              <span>{isBoth ? `${fr.eyebrow} · ${en.eyebrow}` : (lang === "en" ? en.eyebrow : fr.eyebrow)}</span>
            </span>
            <h2 className="mt-5 h-display text-black text-[36px] sm:text-[44px] md:text-[60px] leading-[0.95]">
              {lang === "en" ? "Real talk." : "Ils en parlent."}
              {isBoth && (
                <span className="block mt-2 font-display italic text-xl sm:text-2xl md:text-3xl text-black/55 normal-case">
                  Real talk.
                </span>
              )}
            </h2>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={20} strokeWidth={2} fill="#FFC107" stroke="#FFC107" />
              ))}
            </div>
            <span className="font-display text-lg uppercase">4.8 / 5</span>
          </div>
        </div>

        {/* Light card with yellow accent */}
        <div className="relative rounded-[24px] border border-[var(--bb-grey-line)] bg-[var(--bb-grey)] p-7 md:p-12 lg:p-16">
          <Quote
            size={64}
            strokeWidth={1}
            className="absolute top-6 left-6 md:top-8 md:left-8 text-[var(--bb-yellow)] opacity-90"
            fill="currentColor"
          />

          <div className="relative min-h-[200px] md:min-h-[240px] mt-8 md:mt-4">
            <AnimatePresence mode="wait">
              <motion.blockquote
                key={index + lang}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                data-testid="testimonial-current"
                className="max-w-4xl pl-0 md:pl-12"
              >
                <p className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-[1.15] text-black uppercase">
                  {isBoth ? fr.items[index].q : current.q}
                </p>
                {isBoth && altCurrent && (
                  <p className="mt-3 font-display text-xl md:text-2xl italic text-black/55 leading-tight">
                    {altCurrent.q}
                  </p>
                )}
                <footer className="mt-7 flex items-center gap-3">
                  <span className="h-9 w-9 rounded-full bg-[var(--bb-yellow)] text-black flex items-center justify-center font-display text-sm">
                    {current.n.charAt(0)}
                  </span>
                  <div>
                    <div className="font-display text-base uppercase tracking-wide text-black">
                      {current.n}
                    </div>
                    <div className="text-[11px] tracking-[0.18em] uppercase text-black/55 font-bold mt-0.5">
                      {current.c}
                    </div>
                  </div>
                </footer>
              </motion.blockquote>
            </AnimatePresence>
          </div>

          <div className="mt-8 flex items-center justify-between gap-5">
            <div className="flex items-center gap-2">
              {items.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setIndex(i)}
                  data-testid={`testimonial-dot-${i}`}
                  aria-label={`Testimonial ${i + 1}`}
                  className={`h-1 transition-all duration-300 rounded-full ${
                    i === index
                      ? "w-10 bg-black"
                      : "w-5 bg-black/20 hover:bg-black/40"
                  }`}
                />
              ))}
              <span className="ml-3 text-[11px] tracking-[0.22em] uppercase text-black/45 font-bold">
                {String(index + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={prev}
                data-testid="testimonial-prev"
                aria-label="Previous testimonial"
                className="h-10 w-10 md:h-11 md:w-11 rounded-full border-2 border-black hover:bg-[var(--bb-yellow)] flex items-center justify-center transition-colors text-black"
              >
                <ChevronLeft size={18} strokeWidth={2.5} />
              </button>
              <button
                type="button"
                onClick={next}
                data-testid="testimonial-next"
                aria-label="Next testimonial"
                className="h-10 w-10 md:h-11 md:w-11 rounded-full border-2 border-black hover:bg-[var(--bb-yellow)] flex items-center justify-center transition-colors text-black"
              >
                <ChevronRight size={18} strokeWidth={2.5} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
